// Combined statistics service for fetching data from both CPAI and pentest-api servers
import authService from './authService';
// New import for AI models/usage via CPAI gateway
import rtoService from './rtoService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://138.199.234.61:8080';
const PENTEST_API_PREFIX = process.env.REACT_APP_PENTEST_GATEWAY_PREFIX || '/api/pentest';

function joinBaseAndPrefix(base, prefix) {
  const b = (base || '').replace(/\/+$/, '');
  let p = (prefix || '').replace(/^\/+/, '/');
  if (!p.startsWith('/')) p = `/${p}`;
  if (b.endsWith('/api') && p.startsWith('/api/')) {
    return `${b.slice(0, -4)}${p}`;
  }
  return `${b}${p}`;
}

const PENTEST_API_BASE_URL = joinBaseAndPrefix(API_BASE, PENTEST_API_PREFIX);

class StatisticsService {
    // Make authenticated call to pentest-api server
    async pentestAuthenticatedCall(endpoint, options = {}) {
        const token = authService.getToken();
        const userInfo = authService.getCurrentUser();
        
        if (!token) {
            throw new Error('No authentication token available');
        }

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers
        };

        // Add user info headers for pentest-api server
        if (userInfo) {
            headers['x-user-name'] = userInfo.username;
            headers['x-user-id'] = userInfo.id;
            headers['x-user-role'] = userInfo.role;
        }

        const response = await fetch(`${PENTEST_API_BASE_URL}${endpoint}`, {
            ...options,
            headers
        });

        if (response.status === 401) {
            authService.logout();
            window.location.href = '/pages/login';
            throw new Error('Authentication failed');
        }

        return response;
    }

    // Get combined statistics from both servers
    async getCombinedStatistics() {
        try {
            console.log('Fetching statistics from both servers...');
            
            // Try to fetch CPAI data first
            let cpaiData = {};
            try {
                // authService returns axios data already parsed
                const rawCpaiData = await authService.getStatistics();
                // Extract data from nested success/data structure if present
                cpaiData = rawCpaiData?.success ? rawCpaiData.data : rawCpaiData;
                console.log('CPAI data fetched successfully:', cpaiData);
            } catch (cpaiError) {
                console.error('Error fetching CPAI statistics:', cpaiError);
                cpaiData = {
                    total_users: 0,
                    active_sessions: 0,
                    login_attempts_24h: { total: 0, successful: 0, failed: 0 },
                    users_by_role: {},
                    top_ip_addresses: [],
                    top_endpoints: [],
                    activity_by_day: [],
                    most_active_users: []
                };
            }

            // Try to fetch pentest data
            let pentestData = {};
            let pentestPerformanceData = {};
            let failedScansData = {};
            
            try {
                console.log('Fetching pentest statistics...');
                const pentestResponse = await this.pentestAuthenticatedCall('/auth/statistics');
                const rawPentestData = await pentestResponse.json();
                // Extract data from nested success/data structure if present
                pentestData = rawPentestData?.success ? rawPentestData.data : rawPentestData;
                console.log('Pentest data fetched successfully:', pentestData);
                
                try {
                    const pentestPerformanceResponse = await this.pentestAuthenticatedCall('/auth/statistics/performance');
                    const rawPerfData = await pentestPerformanceResponse.json();
                    // Extract data from nested success/data structure
                    pentestPerformanceData = rawPerfData?.success ? rawPerfData.data : rawPerfData;
                    console.log('Pentest performance data fetched successfully:', pentestPerformanceData);
                } catch (perfError) {
                    console.error('Error fetching pentest performance data:', perfError);
                }
                
                try {
                    const failedScansResponse = await this.pentestAuthenticatedCall('/auth/statistics/failed-scans');
                    const rawFailedData = await failedScansResponse.json();
                    // Extract data from nested success/data structure
                    failedScansData = rawFailedData?.success ? rawFailedData.data : rawFailedData;
                    console.log('Failed scans data fetched successfully:', failedScansData);
                } catch (failedError) {
                    console.error('Error fetching failed scans data:', failedError);
                }
                
            } catch (pentestError) {
                console.error('Error fetching pentest statistics:', pentestError);
                pentestData = {
                    total_scans: 0,
                    scans_by_status: {},
                    unique_users: 0,
                    activity_by_day: [],
                    most_active_users: [],
                    top_target_urls: [],
                    users_by_role: {},
                    recent_scans: []
                };
            }

            // Combine statistics from both servers
            const combinedStats = {
                // CPAI server data (user management, authentication)
                total_users: cpaiData.total_users || 0,
                active_sessions: cpaiData.active_sessions || 0,
                login_attempts_24h: cpaiData.login_attempts_24h || { total: 0, successful: 0, failed: 0 },
                users_by_role: cpaiData.users_by_role || {},
                top_ip_addresses: cpaiData.top_ip_addresses || [],
                top_endpoints: cpaiData.top_endpoints || [],
                activity_by_day: cpaiData.activity_by_day || [],
                most_active_users: cpaiData.most_active_users || [],
                
                // Pentest-api server data (scan statistics)
                pentest: {
                    total_scans: pentestData.total_scans || 0,
                    scans_by_status: pentestData.scans_by_status || {},
                    unique_users: pentestData.unique_users || 0,
                    activity_by_day: pentestData.activity_by_day || [],
                    most_active_users: pentestData.most_active_users || [],
                    top_target_urls: pentestData.top_target_urls || [],
                    users_by_role: pentestData.users_by_role || {},
                    recent_scans: pentestData.recent_scans || [],
                    avg_scan_duration_minutes: pentestData.avg_scan_duration_minutes || 0,
                    failed_scans_details: pentestData.failed_scans_details || [],
                    scan_duration_distribution: pentestData.scan_duration_distribution || [],
                    user_success_rates: pentestData.user_success_rates || [],
                    performance_metrics: pentestPerformanceData || {},
                    failed_scans_analysis: failedScansData || {}
                }
            };

            console.log('Combined statistics prepared:', combinedStats);
            return { data: combinedStats };
        } catch (error) {
            console.error('Error fetching combined statistics:', error);
            throw error;
        }
    }

    // Get combined activity logs from both servers
    async getCombinedActivityLogs(userId = null, limit = 50, offset = 0) {
        try {
            const [cpaiData, pentestResponse] = await Promise.all([
                authService.getActivityLogs({
                    ...(userId && { user_id: userId }),
                    limit: Math.ceil(limit / 2),
                    offset
                }),
                this.pentestAuthenticatedCall(`/auth/activity-logs?${new URLSearchParams({
                    limit: Math.ceil(limit / 2),
                    offset,
                    ...(userId && { user_id: userId })
                })}`)
            ]);

            const pentestRaw = await pentestResponse.json();

            // Normalize to arrays
            const cpaiLogs = Array.isArray(cpaiData)
                ? cpaiData
                : (cpaiData?.logs && Array.isArray(cpaiData.logs))
                    ? cpaiData.logs
                    : (cpaiData?.data && Array.isArray(cpaiData.data))
                        ? cpaiData.data
                        : [];

            const pentestLogs = Array.isArray(pentestRaw)
                ? pentestRaw
                : (pentestRaw?.logs && Array.isArray(pentestRaw.logs))
                    ? pentestRaw.logs
                    : (pentestRaw?.data && Array.isArray(pentestRaw.data))
                        ? pentestRaw.data
                        : [];

            // Robust timestamp parser (handles 'YYYY-MM-DD HH:MM:SS' and multiple field names)
            const toMillis = (item) => {
                const ts = item?.timestamp || item?.created_at || item?.createdAt || item?.time || item?.date;
                if (!ts) return 0;
                if (ts instanceof Date) return ts.getTime();
                if (typeof ts === 'number') return ts; // already millis/seconds (assume millis)
                const base = String(ts).trim();
                const isoLike = base.includes('T') ? base : base.replace(' ', 'T');
                let d = new Date(isoLike);
                if (!isNaN(d.getTime())) return d.getTime();
                // Try forcing Z (UTC) if missing
                d = new Date(isoLike.endsWith('Z') ? isoLike : `${isoLike}Z`);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            };

            // Combine and sort activity logs by timestamp
            const combinedLogs = [
                ...cpaiLogs,
                ...pentestLogs
            ].sort((a, b) => toMillis(b) - toMillis(a))
             .slice(0, limit);

            return { data: combinedLogs };
        } catch (error) {
            console.error('Error fetching combined activity logs:', error);
            throw error;
        }
    }

    // Get combined login attempts from both servers
    async getCombinedLoginAttempts(limit = 50, offset = 0) {
        try {
            const [cpaiData, pentestResponse] = await Promise.all([
                authService.getLoginAttempts({
                    limit: Math.ceil(limit / 2),
                    offset
                }),
                this.pentestAuthenticatedCall(`/auth/login-attempts?${new URLSearchParams({
                    limit: Math.ceil(limit / 2),
                    offset
                })}`)
            ]);

            const pentestRaw = await pentestResponse.json();

            // Normalize to arrays
            const cpaiAttempts = Array.isArray(cpaiData)
                ? cpaiData
                : (cpaiData?.attempts && Array.isArray(cpaiData.attempts))
                    ? cpaiData.attempts
                    : (cpaiData?.data && Array.isArray(cpaiData.data))
                        ? cpaiData.data
                        : [];

            const pentestAttempts = Array.isArray(pentestRaw)
                ? pentestRaw
                : (pentestRaw?.attempts && Array.isArray(pentestRaw.attempts))
                    ? pentestRaw.attempts
                    : (pentestRaw?.data && Array.isArray(pentestRaw.data))
                        ? pentestRaw.data
                        : [];

            // Robust timestamp parser shared with activity logs
            const toMillis = (item) => {
                const ts = item?.timestamp || item?.created_at || item?.createdAt || item?.time || item?.date;
                if (!ts) return 0;
                if (ts instanceof Date) return ts.getTime();
                if (typeof ts === 'number') return ts;
                const base = String(ts).trim();
                const isoLike = base.includes('T') ? base : base.replace(' ', 'T');
                let d = new Date(isoLike);
                if (!isNaN(d.getTime())) return d.getTime();
                d = new Date(isoLike.endsWith('Z') ? isoLike : `${isoLike}Z`);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            };

            // Combine and sort login attempts by timestamp
            const combinedAttempts = [
                ...cpaiAttempts,
                ...pentestAttempts
            ].sort((a, b) => toMillis(b) - toMillis(a))
             .slice(0, limit);

            return { data: combinedAttempts };
        } catch (error) {
            console.error('Error fetching combined login attempts:', error);
            throw error;
        }
    }

    async getAICostAnalytics(params = {}) {
      try {
        let models = [];
        try {
          const data = await rtoService.getAIModels();
          models = Array.isArray(data?.models) ? data.models : (Array.isArray(data) ? data : []);
        } catch (e) {
          if (e?.response?.status === 404) {
            return { data: { available: false, message: 'AI models endpoint not available' } };
          }
          throw e;
        }
    
        let usageRaw = [];
        try {
          const u = await rtoService.getAIUsage(params);
          usageRaw = Array.isArray(u?.usage) ? u.usage : (Array.isArray(u?.data) ? u.data : (Array.isArray(u) ? u : []));
        } catch (e) {
          if (e?.response?.status === 404) {
            return { data: { available: false, message: 'AI usage endpoint not available' } };
          }
          throw e;
        }
    
        // Build model pricing map with robust fallbacks
        const modelPriceMap = {};
        let currency = 'EUR'; // default to EUR per System Settings requirement
        models.forEach((m) => {
          const name = m.name || m.id || m.model || m.slug;
          if (!name) return;
          const cur = (m.currency || currency || 'EUR').toUpperCase();
          currency = cur; // adopt last seen currency

          // Normalize pricing fields
          const inputPerMillion =
            m.input_cost_per_million ?? m.input ?? m.prompt_cost_per_million ??
            (m.input_cost_per_1m ?? m.prompt_cost_per_1m) ??
            (typeof m.input_cost_per_1k === 'number' ? m.input_cost_per_1k * 1000 : undefined) ??
            (typeof m.price_per_1k === 'number' ? m.price_per_1k * 1000 : undefined) ?? 0;
          const outputPerMillion =
            m.output_cost_per_million ?? m.output ?? m.completion_cost_per_million ??
            (m.output_cost_per_1m ?? m.completion_cost_per_1m) ??
            (typeof m.output_cost_per_1k === 'number' ? m.output_cost_per_1k * 1000 : undefined) ??
            (typeof m.price_per_1k === 'number' ? m.price_per_1k * 1000 : undefined) ?? 0;

          modelPriceMap[name] = {
            currency: cur,
            in: Number(inputPerMillion) || 0,
            out: Number(outputPerMillion) || 0,
          };
        });
    
        const million = 1000000;
        const totals = { tokens_in: 0, tokens_out: 0, cost_in_usd: 0, cost_out_usd: 0, cost_total_usd: 0 };
        const by_model = {};
        const per_org_map = {};
        const per_user_map = {};
    
        const getTokens = (rec, keys) => {
          for (const k of keys) {
            const v = rec?.[k];
            if (typeof v === 'number' && !Number.isNaN(v)) return v;
          }
          return 0;
        };
        const getOrg = (rec) => rec.org || rec.organization || rec.organisation || rec.org_id || rec.orgName || 'unknown';
        const getModel = (rec) => rec.model || rec.model_name || rec.model_id || rec.modelId || 'unknown';
        const getUser = (rec) => rec.user || rec.username || rec.user_name || rec.user_id || rec.uid || 'unknown';
    
        usageRaw.forEach((rec) => {
          const org = String(getOrg(rec));
          const model = String(getModel(rec));
          const user = String(getUser(rec));
          const promptTokens = getTokens(rec, ['prompt_tokens', 'input_tokens', 'tokens_in', 'input']);
          const completionTokens = getTokens(rec, ['completion_tokens', 'output_tokens', 'tokens_out', 'output']);
    
          const pricing = modelPriceMap[model] || { currency, in: 0, out: 0 };
          const costIn = (promptTokens / million) * pricing.in;
          const costOut = (completionTokens / million) * pricing.out;
          const total = costIn + costOut;
    
          totals.tokens_in += promptTokens;
          totals.tokens_out += completionTokens;
          totals.cost_in_usd += costIn;
          totals.cost_out_usd += costOut;
          totals.cost_total_usd += total;
    
          if (!by_model[model]) {
            by_model[model] = { tokens_in: 0, tokens_out: 0, cost_total_usd: 0 };
          }
          by_model[model].tokens_in += promptTokens;
          by_model[model].tokens_out += completionTokens;
          by_model[model].cost_total_usd += total;
    
          if (!per_org_map[org]) {
            per_org_map[org] = { org, tokens_in: 0, tokens_out: 0, cost_total_usd: 0, by_model: {}, users_count: 0, users: {} };
          }
          per_org_map[org].tokens_in += promptTokens;
          per_org_map[org].tokens_out += completionTokens;
          per_org_map[org].cost_total_usd += total;
    
          if (!per_org_map[org].by_model[model]) {
            per_org_map[org].by_model[model] = { tokens_in: 0, tokens_out: 0, cost_total_usd: 0 };
          }
          per_org_map[org].by_model[model].tokens_in += promptTokens;
          per_org_map[org].by_model[model].tokens_out += completionTokens;
          per_org_map[org].by_model[model].cost_total_usd += total;

          // Per-user aggregation (org + username)
          const userKey = `${org}::${user}`;
          if (!per_user_map[userKey]) {
            per_user_map[userKey] = { user, org, tokens_in: 0, tokens_out: 0, cost_total_usd: 0 };
          }
          per_user_map[userKey].tokens_in += promptTokens;
          per_user_map[userKey].tokens_out += completionTokens;
          per_user_map[userKey].cost_total_usd += total;

          // Track unique users per org
          if (!per_org_map[org].users[user]) {
            per_org_map[org].users[user] = true;
            per_org_map[org].users_count += 1;
          }
        });
    
        const per_org = Object.values(per_org_map).map((o) => {
          // convert users object to count only in final payload
          const { users, ...rest } = o;
          return rest;
        }).sort((a, b) => b.cost_total_usd - a.cost_total_usd);
        const per_user = Object.values(per_user_map).sort((a, b) => b.cost_total_usd - a.cost_total_usd);
    
        return { data: { available: true, currency, totals, per_org, per_user, by_model } };
      } catch (error) {
        console.error('Error computing AI cost analytics:', error);
        throw error;
      }
    }
}

// Export singleton instance
const statisticsService = new StatisticsService();
export default statisticsService;